import React, { type ReactNode, type SyntheticEvent, useCallback, useMemo, useState } from 'react';

import { Breadcrumbs, Card, CardContent, Divider, Grid, Paper, Skeleton, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { ClassDetailClassSessions } from './ClassDetailClassSessions';
import { ClassDetailEditForm, RequiredIcon } from './ClassDetailEditForm';
import { ClassDetailInsights } from './ClassDetailInsights';
import { ClassDetailLoadingForm } from './ClassDetailLoadingForm';
import { ClassDetailRoster } from './ClassDetailRoster';
import { ClassDetailStudy } from './ClassDetailStudy';
import { useQuery } from '../../../../../hooks/useQuery';
import AdminAuthContainer from '../../../AdminAuthContainer';
import { ClassProvider } from '../../../ClassContext';
import ClassContainer from '../../../ClassModal/ClassContainer';
import ErrorForm from '../../../ClassModal/ErrorForm';
import { RosterProvider } from '../../../RosterContext';
import StyledFormTitle from '../../../styles/form-title';

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

interface TabPanelProps {
    children?: ReactNode;
    dir?: string;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`class-detail-tabpanel-${index}`}
            aria-labelledby={`class-detail-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ mt: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `class-detail-tab-${index}`,
        'aria-controls': `class-detail-tabpanel-${index}`,
        value: index
    };
}

const Title = styled(StyledFormTitle)`
    margin-bottom: 1rem;
`;

export const ClassDetail = () => {
    const { t } = useTranslation(['portal', 'create_edit_class']);
    const { classRecord, handleSubmit, updateClass, getClassToEditStatus, errorMessage } = useContainer(ClassContainer);
    const { authMode } = useContainer(AdminAuthContainer);
    const query = useQuery();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const startingTab = {
        student: 2,
        customerSupport: 0,
        admin: 0
    };
    const [tabValue, setTabValue] = useState(startingTab[authMode] || 0);

    const onSubmit = useCallback(async () => {
        await updateClass({
            onSuccess: () => enqueueSnackbar(t('portal:classes-page.update-success'), { variant: 'success' }),
            onError: () => enqueueSnackbar(t('portal:classes-page.update-error'), { variant: 'error' })
        });
    }, [enqueueSnackbar, t, updateClass]);

    const renderedForm = useMemo(() => {
        if (getClassToEditStatus === 'SUCCESS') {
            return <ClassDetailEditForm onSubmit={handleSubmit(onSubmit)} />;
        }
        if (getClassToEditStatus === 'ERROR') {
            return <ErrorForm errorMessage={errorMessage} />;
        }

        return <ClassDetailLoadingForm />;
    }, [getClassToEditStatus, handleSubmit, onSubmit, errorMessage]);

    const handleTabChange = (event: SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        console.log('value', newValue);
        console.log('event', event);
    };

    if (!classRecord) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    return (
        <>
            <Grid container justifyContent="space-between">
                <Grid item>
                    <Title>{t('portal:classes-page.all-classes')}</Title>
                </Grid>
            </Grid>

            <Breadcrumbs aria-label="breadcrumb">
                <Link to="/portal/classes">{t('portal:classes-page.all-classes')}</Link>
                <Typography color="text.primary">{classRecord?.class_name || query.get('className')}</Typography>
            </Breadcrumbs>

            <Paper elevation={0} sx={{ mt: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    aria-label={t('portal:classes-page.class-detail-tabs')}
                >
                    <Tab label={t('portal:classes-page.class-sessions')} {...a11yProps(0)} />
                    {['instructor'].includes(authMode) && (
                        <Tab label={t('portal:classes-page.details')} {...a11yProps(1)} />
                    )}
                    {[''].includes(authMode) && <Tab label={t('portal:classes-page.roster')} {...a11yProps(2)} />}
                    {[''].includes(authMode) && <Tab label={t('portal:classes-page.insights')} {...a11yProps(3)} />}
                    {['student', 'instructor'].includes(authMode) && (
                        <Tab label={t('portal:classes-page.study')} {...a11yProps(4)} />
                    )}
                </Tabs>
            </Paper>

            <ClassProvider classUuid={classRecord?.class_uuid || ''}>
                <TabPanel value={tabValue} index={0} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>
                            <ClassDetailClassSessions />
                        </CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={1} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>
                            <Typography>
                                <RequiredIcon /> {t('portal:classes-page.required-fields')}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            {renderedForm}
                        </CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={2} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>
                            <RosterProvider
                                classId={classRecord.sid as unknown as string}
                                classUuid={classRecord.class_uuid!}
                                userUuid={window.localStorage?.user_uuid as string}
                                isDemo={'false'}
                            >
                                <ClassDetailRoster />
                            </RosterProvider>
                        </CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={3} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>
                            <ClassDetailInsights />
                        </CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={4} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>
                            <ClassDetailStudy />
                        </CardContent>
                    </Card>
                </TabPanel>
            </ClassProvider>
        </>
    );
};
